import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { Avatar, DatePicker, Tag } from "antd";
import Select from "react-select";
import ApiService from "../../util/ApiService";
import AuthService from "../../login/AuthService";
import HospitalSearch from "../../hospital/HospitalSearch";
import CustomPagination from "../../util/CustomPagination";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/pt_BR";
import { formatDistanceToNow, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import "./assets/css/DutyControl.css";

const { RangePicker } = DatePicker;

const statusOptions = [
    { value: "", label: "Todos os Status" },
    { value: "iniciado", label: "Iniciados" },
    { value: "finalizado", label: "Finalizados" },
];

const DutyControl = () => {
    document.title = "Controle de Plantões | MEDR";

    const user = AuthService.getUserData();
    const today = dayjs();
    const endOfMonth = dayjs().endOf("month");

    const [loading, setLoading] = useState(false);
    const [duties, setDuties] = useState([]);
    const [summary, setSummary] = useState({
        total_iniciados: 0,
        total_finalizados: 0,
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        lastPage: 0,
    });
    const [filters, setFilters] = useState({
        dateRange: [today, endOfMonth],
        datestart: today.format("YYYY-MM-DD"),
        dateend: endOfMonth.format("YYYY-MM-DD"),
        status: "",
        hospital: "",
    });

    useEffect(() => {
        fetchDuties(1);
    }, []);

    const fetchDuties = async (page = 1) => {
        setLoading(true);
        try {
            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}duty/control`,
                {
                    page,
                    per_page: pagination.pageSize,
                    status: filters.status,
                    hospital: filters.hospital,
                    datestart: filters.datestart,
                    dateend: filters.dateend,
                },
                true
            );

            const { data, pagination: pagInfo, extras } = ApiService.handleApiResponse(response);
            setDuties(data || []);
            setPagination(pagInfo || { current: 1, pageSize: 10, total: 0 });

            if (extras?.summary) {
                setSummary(extras.summary);
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleClearFilters = () => {
        setFilters({
            dateRange: [today, endOfMonth],
            datestart: today.format("YYYY-MM-DD"),
            dateend: endOfMonth.format("YYYY-MM-DD"),
            status: "",
            hospital: "",
        });
    };

    const onHospitalSelect = (hospital) => {
        handleFilterChange("hospital", hospital?.value || "");
    };

    const getElapsedTime = (dateTime) => {
        if (!dateTime) return "-";
        try {
            return formatDistanceToNow(new Date(dateTime), {
                addSuffix: true,
                locale: ptBR,
            });
        } catch {
            return "-";
        }
    };

    const formatDateTime = (dateTime) => {
        if (!dateTime) return "-";
        try {
            return format(new Date(dateTime), "dd/MM/yyyy HH:mm", { locale: ptBR });
        } catch {
            return "-";
        }
    };

    const renderSummaryCards = () => (
        <Row className="mb-4">
            <Col md={6} lg={3}>
                <Card className="summary-card iniciado">
                    <CardBody>
                        <div className="d-flex align-items-center">
                            <div className="icon-wrapper me-3">
                                <i className="ri-play-circle-line"></i>
                            </div>
                            <div>
                                <h3 className="mb-0">{summary.total_iniciados}</h3>
                                <span className="text-muted">Plantões Iniciados</span>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </Col>
            <Col md={6} lg={3}>
                <Card className="summary-card finalizado">
                    <CardBody>
                        <div className="d-flex align-items-center">
                            <div className="icon-wrapper me-3">
                                <i className="ri-check-double-line"></i>
                            </div>
                            <div>
                                <h3 className="mb-0">{summary.total_finalizados}</h3>
                                <span className="text-muted">Plantões Finalizados</span>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );

    const renderFilters = () => (
        <div className="filter-section">
            <Row className="align-items-end">
                <Col md={3}>
                    <div className="form-group">
                        <label>Período</label>
                        <div className="form-control">
                            <RangePicker
                                style={{ border: "none", boxShadow: "none", width: "100%" }}
                                locale={locale}
                                format="DD/MM/YYYY"
                                value={filters.dateRange.length > 0 ? filters.dateRange : null}
                                onChange={(dates) => {
                                    if (dates) {
                                        handleFilterChange("dateRange", dates);
                                        handleFilterChange("datestart", dayjs(dates[0]).format("YYYY-MM-DD"));
                                        handleFilterChange("dateend", dayjs(dates[1]).format("YYYY-MM-DD"));
                                    } else {
                                        handleFilterChange("dateRange", []);
                                        handleFilterChange("datestart", null);
                                        handleFilterChange("dateend", null);
                                    }
                                }}
                            />
                        </div>
                    </div>
                </Col>
                <Col md={3}>
                    <HospitalSearch
                        buttonAdd={false}
                        cache={false}
                        all={user?.users_type_id === 1}
                        validateManager={true}
                        onHospitalSelect={onHospitalSelect}
                    />
                </Col>
                <Col md={2}>
                    <div className="form-group">
                        <label>Status</label>
                        <Select
                            options={statusOptions}
                            value={statusOptions.find((opt) => opt.value === filters.status)}
                            onChange={(opt) => handleFilterChange("status", opt?.value || "")}
                            placeholder="Selecione..."
                            isClearable
                        />
                    </div>
                </Col>
                <Col md={4} className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-light" onClick={handleClearFilters}>
                        <i className="fa fa-eraser me-1"></i> Limpar
                    </button>
                    <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => fetchDuties(1)}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Buscando...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-search me-1"></i> Buscar
                            </>
                        )}
                    </button>
                </Col>
            </Row>
        </div>
    );

    const renderDutyCard = (duty) => (
        <Col lg={12} key={duty.id}>
            <Card className="mb-2 duty-list-card">
                <CardBody className="p-3">
                    <Row className="align-items-center">
                        {/* Status Badge */}
                        <Col xs={12} md={1} className="text-center mb-2 mb-md-0">
                            <span className={`status-badge ${duty.presence_status}`}>
                                {duty.presence_status === "iniciado" ? "Iniciado" : "Finalizado"}
                            </span>
                        </Col>

                        {/* Profissional */}
                        <Col xs={12} md={3}>
                            <div className="d-flex align-items-center">
                                <Avatar
                                    src={
                                        duty.accept_user?.profile_image ||
                                        process.env.REACT_APP_URL_STORAGE + "src/perfil_icone.png"
                                    }
                                    size={36}
                                    className="user-avatar me-2"
                                />
                                <div>
                                    <strong className="d-block">{duty.accept_user?.name || "Não atribuído"}</strong>
                                    <small className="text-muted">
                                        <i className="fa fa-tag me-1"></i>#{duty.id}
                                    </small>
                                </div>
                            </div>
                        </Col>

                        {/* Hospital */}
                        <Col xs={12} md={3}>
                            <div className="d-flex align-items-center">
                                <Avatar
                                    src={
                                        duty.hospital?.images?.[0]?.file ||
                                        `${process.env.REACT_APP_URL_STORAGE}src/hospital_icone.png`
                                    }
                                    size={40}
                                    className="hospital-avatar me-2"
                                />
                                <div>
                                    <strong className="d-block" style={{ fontSize: "13px" }}>
                                        {duty.hospital?.name?.split(" ").slice(0, 3).join(" ")}
                                    </strong>
                                    <small className="text-muted">
                                        <i className="fa fa-map-marker-alt me-1"></i>
                                        {duty.hospital?.city?.name}, {duty.hospital?.uf}
                                    </small>
                                </div>
                            </div>
                        </Col>

                        {/* Tempo Decorrido */}
                        <Col xs={12} md={3}>
                            <div className="checkin-info">
                                <div className="mb-1">
                                    <span className="time-label">Check-in:</span>{" "}
                                    {formatDateTime(duty.checkin_at)}
                                    <span className="ms-2 elapsed-time">
                                        <i className="ri-time-line"></i>
                                        {getElapsedTime(duty.checkin_at)}
                                    </span>
                                </div>
                                {duty.checkout_at && (
                                    <div>
                                        <span className="time-label">Check-out:</span>{" "}
                                        {formatDateTime(duty.checkout_at)}
                                    </div>
                                )}
                                {duty.valid_location !== undefined && (
                                    <span
                                        className={`location-badge mt-1 d-inline-block ${
                                            duty.valid_location ? "valid" : "invalid"
                                        }`}
                                    >
                                        <i
                                            className={`ri-${
                                                duty.valid_location ? "map-pin-line" : "map-pin-line"
                                            } me-1`}
                                        ></i>
                                        {duty.valid_location ? "Localização válida" : "Fora da área"}
                                    </span>
                                )}
                            </div>
                        </Col>

                        {/* Data e Especialidades */}
                        <Col xs={12} md={2} className="text-end">
                            <p className="text-muted mb-1" style={{ fontSize: "12px" }}>
                                <i className="fa fa-calendar me-1"></i>
                                {new Date(duty.date_time).toLocaleDateString("pt-BR")}{" "}
                                {new Date(duty.date_time).toLocaleTimeString("pt-BR", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                            </p>
                            <div>
                                {duty.specialties?.slice(0, 2).map((s) => (
                                    <Tag key={s.id} color="blue" style={{ fontSize: "10px" }}>
                                        {s.name}
                                    </Tag>
                                ))}
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        </Col>
    );

    const renderSkeleton = () => (
        <>
            {[1, 2, 3].map((i) => (
                <Col lg={12} key={i}>
                    <Card className="mb-2">
                        <CardBody className="p-3">
                            <Row className="align-items-center">
                                <Col md={1}>
                                    <Skeleton width={80} height={24} />
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center">
                                        <Skeleton circle width={36} height={36} className="me-2" />
                                        <div>
                                            <Skeleton width={120} />
                                            <Skeleton width={60} />
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <Skeleton width={150} />
                                </Col>
                                <Col md={3}>
                                    <Skeleton width={180} height={60} />
                                </Col>
                                <Col md={2}>
                                    <Skeleton width={100} />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            ))}
        </>
    );

    return (
        <div className="page-content duty-control-page">
            <Container fluid>
                <BreadCrumb title="Controle" pageTitle="Plantões" />

                {renderSummaryCards()}
                {renderFilters()}

                <Row className="gy-2">
                    {loading ? (
                        renderSkeleton()
                    ) : duties.length > 0 ? (
                        duties.map((duty) => renderDutyCard(duty))
                    ) : (
                        <Col lg={12}>
                            <div className="alert alert-info">
                                <i className="ri-information-line me-2"></i>
                                Nenhum plantão encontrado com os filtros selecionados.
                            </div>
                        </Col>
                    )}
                </Row>

                <CustomPagination
                    className="mt-4"
                    pagination={pagination}
                    onChange={(page) => fetchDuties(page)}
                />
            </Container>
        </div>
    );
};

export default DutyControl;
